import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dahgu9rp14a1h3mr6mh0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

type Status = "idle" | "loading" | "success" | "error";

export default function NewsletterForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const trap = String(formData.get("website_alt") ?? "").trim();
    if (trap) {
      form.reset();
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    setStatus("loading");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请稍后重试。"
            : serverMsg || "提交失败，请稍后重试。"
        );
        return;
      }

      form.reset();
      setStatus("success");
      setFormError("");
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id="guaguan-newsletter-form"
      onSubmit={handleSubmit}
      className="w-full"
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label htmlFor="newsletter-email" className="sr-only">
            邮箱地址
          </label>
          <input
            id="newsletter-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="你的邮箱地址"
            className="w-full px-4 py-3 rounded-md border border-primary-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="sm:w-44">
          <label htmlFor="newsletter-topic" className="sr-only">
            关注方向
          </label>
          <select
            id="newsletter-topic"
            name="关注方向"
            className="w-full px-4 py-3 rounded-md border border-primary-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 cursor-pointer"
          >
            <option value="全部栏目">全部栏目</option>
            <option value="打榜观察">打榜观察</option>
            <option value="票数分析">票数分析</option>
            <option value="粉丝生态">粉丝生态</option>
            <option value="赛事规则">赛事规则</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className="px-6 py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "loading" ? "提交中…" : "订阅日报"}
        </button>
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-guard-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <p className="mt-3 text-xs text-foreground-500">
        每工作日一封，只推送经过核对的观察内容，可随时退订。
      </p>

      {status === "success" && (
        <p role="status" className="mt-3 text-sm text-primary-700 flex items-center gap-1.5">
          <i className="ri-check-line" />
          订阅成功，日报会在下一个工作日早晨送到你的邮箱。
        </p>
      )}
      {status === "error" && formError && (
        <p role="alert" className="mt-3 text-sm text-primary-600 flex items-center gap-1.5">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      )}
    </form>
  );
}